// Stammdaten-Formular (Drehkarte „Bearbeiten“ im Panel Technik & Stammdaten).
// Enthält alle Felder des bisherigen „Objekt ändern“ außer Eigentümer und Zuständigkeit –
// die werden in ihren eigenen Panels gepflegt.

import { istGueltigeUrlEingabe } from './url';

export const OBJEKTTYPEN = ['Mehrfamilienhaus', 'Einfamilienhaus', 'Mietverwaltung', 'WEG', 'Sondereigentum', 'Teileigentum', 'Gewerbe', 'Neubau'] as const;
export const HEIZUNGSARTEN = ['Gas', 'Öl', 'Fernwärme', 'Wärmepumpe', 'Pellet', 'Elektro', 'Solar', 'Sonstige'] as const;
export const ENERGIEKLASSEN = ['A+', 'A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'] as const;
export const STELLPLATZ_TYPEN = ['Keine', 'Stellplatz', 'Garage', 'Tiefgarage', 'Carport'] as const;
export const VERWALTUNGSARTEN = ['MV', 'WEG', 'SEV', 'GV'] as const;

export type VerwaltungsartWert = typeof VERWALTUNGSARTEN[number];

export interface StammEntwurf {
  verwaltungsart: VerwaltungsartWert | '';
  bezeichnung: string;
  objektnummer: string;
  objekttyp: string;
  status: 'Aktiv' | 'Inaktiv' | 'Archiviert';
  strasse: string;
  hausnummer: string;
  plz: string;
  stadt: string;
  etage: string;
  baujahr: string;
  wohnflaeche: string;
  wohneinheiten: string;
  miete_soll: string;
  stellplatz_typ: string;
  stellplatz_anzahl: number;
  heizungsart: string;
  energieklasse: string;
  energieausweis: string;
  letzte_pruefung: string;
  ausstattung: string;
  notizen: string;
  onedrive_link: string;
  bild_url: string;
}

type Roh = Record<string, unknown>;

const text = (v: unknown): string => (v === null || v === undefined ? '' : String(v));

/** Zahl in deutscher Schreibweise für ein Eingabefeld: 350.5 → „350,5“. */
function zahlText(v: unknown): string {
  if (v === null || v === undefined || v === '') return '';
  const n = typeof v === 'number' ? v : Number(String(v).replace(',', '.'));
  return Number.isFinite(n) ? String(n).replace('.', ',') : String(v);
}

export function entwurfAusObjekt(o: Roh): StammEntwurf {
  const va = text(o.verwaltungsart).toUpperCase();
  const roherStatus = text(o.status).toLowerCase();
  const status = roherStatus === 'inaktiv' ? 'Inaktiv' : roherStatus === 'archiviert' ? 'Archiviert' : 'Aktiv';
  const anzahl = Number(o.stellplatz_anzahl);
  return {
    verwaltungsart: (VERWALTUNGSARTEN as readonly string[]).includes(va) ? (va as VerwaltungsartWert) : '',
    bezeichnung: text(o.bezeichnung),
    objektnummer: text(o.objektnummer),
    objekttyp: text(o.objekttyp),
    status,
    strasse: text(o.strasse),
    hausnummer: text(o.hausnummer),
    plz: text(o.plz),
    stadt: text(o.stadt),
    etage: text(o.etage),
    baujahr: text(o.baujahr),
    wohnflaeche: zahlText(o.wohnflaeche),
    wohneinheiten: text(o.wohneinheiten),
    miete_soll: zahlText(o.miete_soll),
    stellplatz_typ: text(o.stellplatz_typ) || 'Keine',
    stellplatz_anzahl: Number.isFinite(anzahl) && anzahl > 0 ? Math.round(anzahl) : 0,
    heizungsart: text(o.heizungsart),
    energieklasse: text(o.energieklasse),
    energieausweis: text(o.energieausweis),
    letzte_pruefung: text(o.letzte_pruefung).slice(0, 10),
    ausstattung: text(o.ausstattung),
    notizen: text(o.notizen),
    onedrive_link: text(o.onedrive_link),
    bild_url: text(o.bild_url),
  };
}

/**
 * Dezimalzahl aus deutscher Eingabe – gleiche Regel wie das bisherige Formular:
 * „5.400,50“ → 5400.5, „5400“ → 5400, leer → null.
 */
export function parseDezimal(eingabe: string): number | null {
  let v = eingabe.replace(/[^0-9,.]/g, '');
  if (!v) return null;
  if (v.includes(',')) v = v.replace(/\./g, '').replace(',', '.');
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : null;
}

function ganzzahl(eingabe: string): number | null {
  const n = parseInt(eingabe.replace(/[^0-9-]/g, ''), 10);
  return Number.isFinite(n) ? n : null;
}

const oderNull = (s: string): string | null => (s.trim() === '' ? null : s.trim());

/** Update für objekte – nur bekannte Spalten, Zahlen bereinigt (wie bisher: Einheiten/Stellplätze 0 statt leer). */
export function entwurfZuUpdate(e: StammEntwurf): Record<string, string | number | null> {
  return {
    verwaltungsart: e.verwaltungsart || null,
    bezeichnung: e.bezeichnung.trim(),
    objektnummer: e.objektnummer.trim(),
    objekttyp: oderNull(e.objekttyp),
    status: e.status,
    strasse: e.strasse.trim(),
    hausnummer: e.hausnummer.trim(),
    plz: e.plz.trim(),
    stadt: e.stadt.trim(),
    etage: oderNull(e.etage),
    baujahr: ganzzahl(e.baujahr),
    wohnflaeche: parseDezimal(e.wohnflaeche),
    wohneinheiten: ganzzahl(e.wohneinheiten) ?? 0,
    miete_soll: parseDezimal(e.miete_soll),
    stellplatz_typ: e.stellplatz_typ || 'Keine',
    stellplatz_anzahl: e.stellplatz_typ === 'Keine' ? 0 : e.stellplatz_anzahl,
    heizungsart: oderNull(e.heizungsart),
    energieklasse: oderNull(e.energieklasse),
    energieausweis: oderNull(e.energieausweis),
    letzte_pruefung: oderNull(e.letzte_pruefung),
    ausstattung: oderNull(e.ausstattung),
    notizen: oderNull(e.notizen),
    onedrive_link: oderNull(e.onedrive_link),
    bild_url: oderNull(e.bild_url),
  };
}

/** Was fehlt noch? (Objektnummer und Straße sind Pflicht, Baujahr plausibel.) */
export function entwurfOffen(e: StammEntwurf): string[] {
  const offen: string[] = [];
  if (!e.objektnummer.trim()) offen.push('Objektnummer');
  if (!e.strasse.trim()) offen.push('Straße');
  const bj = ganzzahl(e.baujahr);
  if (e.baujahr.trim() && (bj === null || bj < 1000 || bj > new Date().getFullYear() + 5)) offen.push('gültiges Baujahr');
  if (e.wohnflaeche.trim() && parseDezimal(e.wohnflaeche) === null) offen.push('gültige Wohnfläche');
  if (!istGueltigeUrlEingabe(e.onedrive_link)) offen.push('OneDrive-Link mit https://');
  if (!istGueltigeUrlEingabe(e.bild_url)) offen.push('Bild-URL mit https://');
  return offen;
}
